/**
 * Bazaarvoice inline rating (stars + review count) for PDP only.
 * @see https://docs.bazaarvoice.com/articles/#!ratings-reviews/display_integration
 */
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {useBazaarvoiceScript} from '@amblique/king-living/app/hooks/use-bazaarvoice-script';
import {
  getBazaarvoiceProductId,
  isBazaarvoiceConfigured,
} from '@amblique/king-living/app/utils/bazaarvoice';

/**
 * Format BV `rating_summary` review count to match Figma: "(12 Reviews)".
 * Falls back to offscreen text when BV only renders a bare count.
 *
 * @param {HTMLElement} root
 */
function syncBazaarvoiceReviewCount(root) {
  const reviewsText = root.querySelector('.bv_numReviews_text');

  if (!reviewsText) {
    return;
  }

  const raw = reviewsText.textContent?.trim() || '';

  if (/reviews/i.test(raw)) {
    return;
  }

  const match =
    raw.match(/\(?([\d,]+)\)?/) ||
    root.querySelector('.bv_offscreen_text')?.textContent?.match(/([\d,]+)\s+reviews?\b/i);

  if (!match) {
    return;
  }

  const count = match[1].replace(/,/g, '');
  const formatted = `(${count} Reviews)`;

  if (reviewsText.textContent !== formatted) {
    reviewsText.textContent = formatted;
  }
}

/**
 * @param {object} props
 * @param {object} props.product - SCAPI product or search hit
 * @param {string} [props.redirectUrl] - PDP URL when clicked from PLP (optional on PDP)
 * @param {'default'|'tile'|'strip'} [props.variant] - Layout context (PDP info, product tile, sticky strip)
 * @returns {React.ReactElement|null}
 */
const PDP_INLINE_RATING_VARIANTS = new Set(['default', 'strip']);

export function BazaarvoiceInlineRating({product, redirectUrl, variant = 'default'}) {
  const containerRef = useRef(null);
  const bazaarvoicePrefs = useSitePreference('bazaarvoice');
  const isConfigured = isBazaarvoiceConfigured(bazaarvoicePrefs);
  const productId = getBazaarvoiceProductId(product);
  const styles = useMultiStyleConfig('BazaarvoiceInlineRating', {variant});
  const isBvReady = useBazaarvoiceScript(isConfigured);

  useEffect(() => {
    const root = containerRef.current;

    if (!isBvReady || !root) {
      return undefined;
    }

    syncBazaarvoiceReviewCount(root);

    const observer = new MutationObserver(() => {
      syncBazaarvoiceReviewCount(root);
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-bv-ready'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isBvReady, productId]);
  const isPdpVariant = PDP_INLINE_RATING_VARIANTS.has(variant);

  useBazaarvoiceScript(isConfigured && isPdpVariant);

  if (!isPdpVariant || !isConfigured || !productId) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      className="sf-bazaarvoice-inline-rating"
      data-bv-variant={variant}
      data-testid="bazaarvoice-inline-rating"
      sx={styles.container}
    >
      <div
        data-bv-product-id={productId}
        data-bv-redirect-url={redirectUrl || undefined}
        data-bv-seo="false"
        data-bv-show="rating_summary"
      />
    </Box>
  );
}

BazaarvoiceInlineRating.propTypes = {
  product: PropTypes.object,
  redirectUrl: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'tile', 'strip']),
};

export default BazaarvoiceInlineRating;
